import React, { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiGlobe,
  FiCalendar,
  FiUsers,
  FiBookOpen,
} from "react-icons/fi";
import { BsCheckCircleFill } from "react-icons/bs";
import { MdOutlineVerified } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import ConfirmationModal from "../components/common/ConfirmationModal";
import Footer from "../components/common/Footer";
import RatingStars from "../components/common/RatingStars";
import CourseAccordionBar from "../components/core/Course/CourseAccordionBar";
import CourseDetailsCard from "../components/core/Course/CourseDetailsCard";
import { formatDate } from "../services/formatDate";
import { fetchCourseDetails } from "../services/operations/courseDetailsAPI";
import { buyCourse } from "../services/operations/studentFeaturesAPI";
import GetAvgRating from "../utils/avgRating";
import { ACCOUNT_TYPE } from "../utils/constants";
import { addToCart } from "../slices/cartSlice";
import { DS } from "../utils/designSystem";

/* ── Loading skeleton ─────────────────────────────────────────── */
function Skeleton() {
  const bar = (w, h = "h-4") => (
    <div className={`${h} ${w} animate-pulse rounded-lg bg-richblack-700`} />
  );
  return (
    <div className="min-h-screen bg-richblack-900">
      <div className="border-b border-richblack-700 bg-richblack-800">
        <div className={`${DS.layout.container} py-10`}>
          <div className="flex max-w-3xl flex-col gap-4">
            {bar("w-1/4", "h-3")}
            {bar("w-3/4", "h-10")}
            {bar("w-full", "h-4")}
            {bar("w-2/3", "h-4")}
            {bar("w-1/3", "h-4")}
          </div>
        </div>
      </div>
      <div className={`${DS.layout.container} py-10`}>
        <div className="h-48 animate-pulse rounded-2xl bg-richblack-800" />
      </div>
    </div>
  );
}

function CourseDetails() {
  const { user } = useSelector((s) => s.profile);
  const { token } = useSelector((s) => s.auth);
  const { loading } = useSelector((s) => s.profile);
  const { paymentLoading } = useSelector((s) => s.course);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { courseId } = useParams();

  const [response, setResponse] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [avgReviewCount, setAvgReviewCount] = useState(0);
  const [isActive, setIsActive] = useState([]);
  const [totalLectures, setTotalLectures] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchCourseDetails(courseId);
        setResponse(res);
      } catch {
        toast.error("Could not load course details.");
      }
    })();
  }, [courseId]);

  useEffect(() => {
    setAvgReviewCount(GetAvgRating(response?.data?.courseDetails?.ratingAndReviews));
  }, [response]);

  useEffect(() => {
    let count = 0;
    response?.data?.courseDetails?.courseContent?.forEach(
      (s) => (count += s.subSection?.length || 0)
    );
    setTotalLectures(count);
  }, [response]);

  if (paymentLoading || loading || !response) return <Skeleton />;

  const {
    courseName,
    courseDescription,
    thumbnail,
    price,
    whatYouWillLearn,
    courseContent,
    ratingAndReviews,
    instructor,
    studentsEnrolled,
    createdAt,
    tag,
  } = response.data.courseDetails;

  const isEnrolled =
    user && response.data.courseDetails.studentsEnrolled.includes(user._id);

  const handleBuyCourse = () => {
    if (token) {
      buyCourse(token, [courseId], user, navigate, dispatch);
      return;
    }
    setConfirmationModal({
      text1: "You are not logged in!",
      text2: "Please login to purchase this course.",
      btn1Text: "Log in",
      btn2Text: "Cancel",
      btn1Handler: () => navigate("/login"),
      btn2Handler: () => setConfirmationModal(null),
    });
  };

  const handleAddToCart = () => {
    if (user?.accountType === ACCOUNT_TYPE.INSTRUCTOR) {
      toast.error("Instructors cannot purchase courses.");
      return;
    }
    if (token) {
      dispatch(addToCart(response.data.courseDetails));
      return;
    }
    setConfirmationModal({
      text1: "You are not logged in!",
      text2: "Please login to add to cart.",
      btn1Text: "Log in",
      btn2Text: "Cancel",
      btn1Handler: () => navigate("/login"),
      btn2Handler: () => setConfirmationModal(null),
    });
  };

  const learnItems = whatYouWillLearn
    ?.split("\n")
    .map((l) => l.trim())
    .filter(Boolean) ?? [];

  return (
    <>
      {/* ── HERO BAND ─────────────────────────────────────────────────
          overflow-hidden here is safe: no sticky elements inside.     */}
      <section className="relative overflow-hidden border-b border-richblack-700 bg-richblack-800">
        {/* Ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 right-0 h-[500px] w-[500px] rounded-full bg-[#1FA2FF]/5 blur-3xl"
        />

        <div className={`${DS.layout.container} py-8`}>
          <div className="flex flex-col gap-5 lg:max-w-3xl">

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-richblack-300">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 rounded-lg p-1 transition-colors hover:text-richblack-5"
              >
                <FiArrowLeft />
              </button>
              <Link to="/" className="transition-colors hover:text-richblack-5">
                Home
              </Link>
              <span>/</span>
              <span className="truncate text-richblack-5">{courseName}</span>
            </div>

            {/* Mobile thumbnail */}
            <div className="aspect-video w-full overflow-hidden rounded-2xl bg-richblack-700 lg:hidden">
              <img
                src={thumbnail}
                alt={courseName}
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold leading-snug text-richblack-5 lg:text-4xl">
              {courseName}
            </h1>

            {/* Description */}
            <p className="text-base leading-relaxed text-richblack-200">
              {courseDescription}
            </p>

            {/* Rating row */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-bold text-yellow-50">{avgReviewCount || 0}</span>
              <RatingStars Review_Count={avgReviewCount} Star_Size={16} />
              <span className="text-richblack-300">
                ({ratingAndReviews.length} reviews)
              </span>
              <span className="text-richblack-500">·</span>
              <span className="flex items-center gap-1 text-richblack-300">
                <FiUsers className="flex-shrink-0" />
                {studentsEnrolled.length.toLocaleString()} students
              </span>
            </div>

            {/* Instructor */}
            <p className="text-sm text-richblack-200">
              Created by{" "}
              <span className="font-semibold text-yellow-50 underline underline-offset-2">
                {instructor.firstName} {instructor.lastName}
              </span>
            </p>

            {/* Meta */}
            <div className="flex flex-wrap gap-4 text-sm text-richblack-300">
              <span className="flex items-center gap-1.5">
                <FiCalendar className="flex-shrink-0" />
                {formatDate(createdAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <FiGlobe className="flex-shrink-0" />
                English
              </span>
              <span className="flex items-center gap-1.5">
                <FiBookOpen className="flex-shrink-0" />
                {courseContent.length} sections · {totalLectures} lectures
              </span>
            </div>

            {/* Mobile price + actions */}
            <div className="flex flex-col gap-3 rounded-2xl border border-richblack-700 bg-richblack-900/60 p-5 lg:hidden">
              <p className="text-3xl font-bold text-richblack-5">
                ₹{price?.toLocaleString("en-IN")}
                <span className="ml-1 text-base font-normal text-richblack-400">/-</span>
              </p>
              <button
                onClick={
                  isEnrolled
                    ? () => navigate("/dashboard/enrolled-courses")
                    : handleBuyCourse
                }
                className={`${DS.btn.primary} w-full justify-center`}
              >
                {isEnrolled ? "Go To Course" : "Buy Now"}
              </button>
              {!isEnrolled && (
                <button
                  onClick={handleAddToCart}
                  className={`${DS.btn.secondary} w-full justify-center`}
                >
                  Add to Cart
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── BODY + STICKY CARD ────────────────────────────────────────
          No overflow restriction here — sticky works correctly.       */}
      <div className="bg-richblack-900">
        <div className={DS.layout.container}>
          {/*
            lg:items-start is critical: prevents the right column from
            stretching to the grid height, which would make sticky
            trigger only at the very bottom.
          */}
          <div className="py-10 lg:grid lg:grid-cols-[1fr_340px] lg:items-start lg:gap-10">

            {/* ── Left: body content ── */}
            <div className="flex flex-col gap-10">

              {/* What you'll learn */}
              {learnItems.length > 0 && (
                <div className={`${DS.card.base} p-6 lg:p-8`}>
                  <h2 className="mb-5 text-2xl font-semibold text-richblack-5">
                    What You&apos;ll Learn
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {learnItems.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <BsCheckCircleFill className="mt-0.5 flex-shrink-0 text-sm text-caribbeangreen-100" />
                        <p className="text-sm leading-relaxed text-richblack-100">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {tag?.length > 0 && (
                <div>
                  <h2 className="mb-3 text-lg font-semibold text-richblack-5">Tags</h2>
                  <div className="flex flex-wrap gap-2">
                    {tag.map((t, i) => (
                      <span
                        key={i}
                        className="rounded-full border border-richblack-600 bg-richblack-800 px-4 py-1 text-xs font-medium text-richblack-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Course content */}
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-2xl font-semibold text-richblack-5">
                    Course Content
                  </h2>
                  <button
                    onClick={() => setIsActive([])}
                    className="text-sm font-medium text-yellow-50 transition-colors hover:text-yellow-25"
                  >
                    Collapse All
                  </button>
                </div>
                <p className="mb-5 text-sm text-richblack-400">
                  {courseContent.length} section{courseContent.length !== 1 ? "s" : ""}&nbsp;·&nbsp;
                  {totalLectures} lecture{totalLectures !== 1 ? "s" : ""}&nbsp;·&nbsp;
                  {response.data?.totalDuration} total
                </p>
                <div className="overflow-hidden rounded-2xl border border-richblack-700">
                  {courseContent.map((section, i) => (
                    <CourseAccordionBar
                      key={section._id}
                      course={section}
                      isActive={isActive}
                      handleActive={(id) =>
                        setIsActive((prev) =>
                          prev.includes(id)
                            ? prev.filter((e) => e !== id)
                            : [...prev, id]
                        )
                      }
                      isLast={i === courseContent.length - 1}
                    />
                  ))}
                </div>
              </div>

              {/* Instructor */}
              <div className={`${DS.card.base} p-6`}>
                <h2 className="mb-5 text-2xl font-semibold text-richblack-5">
                  About the Instructor
                </h2>
                <div className="flex items-start gap-4">
                  <img
                    src={
                      instructor.image ||
                      `https://api.dicebear.com/5.x/initials/svg?seed=${instructor.firstName}`
                    }
                    alt={`${instructor.firstName} ${instructor.lastName}`}
                    className="h-16 w-16 flex-shrink-0 rounded-full object-cover ring-2 ring-richblack-600"
                  />
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-lg font-semibold text-richblack-5">
                      {instructor.firstName} {instructor.lastName}
                      <MdOutlineVerified className="flex-shrink-0 text-blue-200" />
                    </p>
                    {instructor.additionalDetails?.about && (
                      <p className="mt-1 text-sm leading-relaxed text-richblack-300">
                        {instructor.additionalDetails.about}
                      </p>
                    )}
                  </div>
                </div>
              </div>

            </div>
            {/* ── end left ── */}

            {/* ── Right: sticky course card ── */}
            <div className="hidden lg:block">
              <div className="sticky top-[5.5rem]">
                <CourseDetailsCard
                  course={response.data.courseDetails}
                  setConfirmationModal={setConfirmationModal}
                  handleBuyCourse={handleBuyCourse}
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
    </>
  );
}

export default CourseDetails;
